import { useCallback } from 'react';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { getTransferrableProps } from '../../../utils/getTransferrableProps';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { getPipelinePropertyName, getPipelineStagePropertyName, allowsEmptyPipelineStage } from '../utils/pipelineUtils';
import { referenceToOptionWithReferenceV2 } from '../../../utils/referenceLiteSearchSelectUtils';
import { CREATE_INPUT_MODE } from '../../../v2/types/PropertyInputV2Component';
import { usePropertyValue } from '../../../v2/hooks/usePropertyValue';
import { useIsBETPortal } from '../../../v2/hooks/useIsBETPortal';
import ReferenceLiteSearchSelectAdapter from '../../../adapters/ReferenceLiteSearchSelect';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
const PipelineSelect = props => {
  const {
    clearable,
    dropdownFooter,
    inputRef,
    onChangeMultiple,
    placeholder,
    inputMode,
    onOpenChange
  } = props;
  const isPortal53 = useIsBETPortal();
  const objectTypeId = useObjectTypeId();
  const httpClient = useHttpClient();
  const value = usePropertyValue();

  // NOTE: we cannot use `usePropertyComponentResolver` because we need
  // `PIPELINE_WITH_PERMISSIONS` to ensure that PLP-hidden pipelines are disabled.
  // Making `PIPELINE_WITH_PERMISSIONS` the default for pipeline properties in
  // `getReferenceTypeFromProperty` (instead of `PIPELINE`) would break
  // filters/workflows by preventing users from selecting these pipelines
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient
  });

  // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).
  const handleSelectedOptionChange = useCallback(e => {
    const newPipelineId = `${e.target.value.value}`;
    if (newPipelineId === value) {
      // ignore repeat selection of the already-selected option
      // onChange does not be called when the user selects the already-
      // selected option, but `onSelectedOptionChange` does and we need
      // to manually ignore it
      return;
    }
    const reference = e.target.value.reference;
    const createStageId = reference && reference.additionalProperties && reference.additionalProperties.createStageId ? `${reference.additionalProperties.createStageId}` : null;
    const defaultStageId = reference && reference.additionalProperties && reference.additionalProperties.defaultPipelineStageId ? `${reference.additionalProperties.defaultPipelineStageId}` : null;
    const newStageId = inputMode === CREATE_INPUT_MODE && createStageId ? createStageId : defaultStageId;
    const changes = isPortal53 ? {
      [getPipelinePropertyName(objectTypeId)]: newPipelineId
    } : {
      [getPipelinePropertyName(objectTypeId)]: newPipelineId,
      [getPipelineStagePropertyName(objectTypeId)]: newStageId
    };
    onChangeMultiple(changes);
  }, [value, inputMode, objectTypeId, onChangeMultiple, isPortal53]);
  return /*#__PURE__*/_jsx(ReferenceLiteSearchSelectAdapter, Object.assign({}, getTransferrableProps(props), {
    referenceToOption: referenceToOptionWithReferenceV2
    // NOTE: using onSelectedOptionChange instead of onChange; see above
    ,
    onChange: undefined,
    onSelectedOptionChange: handleSelectedOptionChange,
    resolver: pipelineResolver,
    inputRef: inputRef,
    anchorType: "button",
    value: value,
    onOpenChange: onOpenChange,
    clearable: clearable !== undefined ? clearable : allowsEmptyPipelineStage(objectTypeId),
    dropdownFooter: dropdownFooter,
    placeholder: placeholder || unescapedText('customerDataProperties.PropertyInputPipelineV2.placeholder')
  }));
};
PipelineSelect.displayName = 'PipelineSelect';
export default PipelineSelect;